import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faTags, faStar, faPercent, faPowerOff, faStopwatch, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../../../../components/Button';
import { happyHourService, type HappyHourView } from '../../../../../../services/happy-hour/happy-hour.service';
import { toHebrewApiErrorMessage } from '../../../../../../services/index/index.service';
import navyModal from '../../../../../../components/Modal/navy-modal.module.scss';
import classes from './happy-hour-active-modal.module.scss';

type HappyHourActiveModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string | null;
  happyHour: HappyHourView | null;
};

const DAY_LABELS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

function formatDays(days: number[]): string {
  return days
    .filter((day) => day >= 0 && day < DAY_LABELS.length)
    .map((day) => DAY_LABELS[day])
    .join(' · ');
}

function getRemainingLabel(endTime: string | null, now: Date): string {
  if (!endTime) {
    return '00:00:00';
  }
  const [hoursRaw, minutesRaw] = endTime.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return '00:00:00';
  }

  const end = new Date(now);
  end.setHours(hours, minutes, 0, 0);
  if (end.getTime() <= now.getTime()) {
    end.setDate(end.getDate() + 1);
  }
  const totalSeconds = Math.max(0, Math.floor((end.getTime() - now.getTime()) / 1000));
  const hh = Math.floor(totalSeconds / 3600);
  const mm = Math.floor((totalSeconds % 3600) / 60);
  const ss = totalSeconds % 60;
  return [hh, mm, ss].map((value) => String(value).padStart(2, '0')).join(':');
}

export const HappyHourActiveModal: React.FC<HappyHourActiveModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
  happyHour,
}) => {
  const saveInFlightRef = useRef(false);
  const [now, setNow] = useState(() => new Date());
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!opened) {
      return undefined;
    }
    setNow(new Date());
    const intervalId = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(intervalId);
  }, [opened]);

  const categoryNames = useMemo(
    () => happyHour?.categories.map((category) => category.name).join(' · ') ?? '',
    [happyHour],
  );
  const remainingLabel = getRemainingLabel(happyHour?.endTime ?? null, now);

  const handleDeactivate = async () => {
    if (!employeeSession || isSaving || saveInFlightRef.current) {
      return;
    }
    saveInFlightRef.current = true;
    setIsSaving(true);
    try {
      await happyHourService.deactivate(tenantId, employeeSession);
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: toHebrewApiErrorMessage(message),
        autoClose: 3000,
      });
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={isSaving ? () => undefined : onClose}
      onContextMenu={(event) => event.preventDefault()}
      withCloseButton={false}
      centered
      size={640}
      radius="md"
      overlayProps={{ opacity: 0.55, blur: 1 }}
      closeOnClickOutside={!isSaving}
      closeOnEscape={!isSaving}
      classNames={{ root: navyModal.noLongPress, content: classes.modalContent, body: classes.modalBody }}
    >
      <header className={classes.header}>
        <span className={classes.heroIcon}>
          <FontAwesomeIcon icon={faStar as IconProp} />
        </span>
        <div className={classes.titleBlock}>
          <div className={classes.titleRow}>
            <h2>Happy Hour פעיל</h2>
            <span className={classes.liveBadge}>LIVE •</span>
          </div>
          <p>המבצע פעיל כרגע על כל המכשירים</p>
        </div>
      </header>

      <div className={classes.content}>
      <section className={classes.infoList}>
        <article className={classes.infoCard}>
          <span className={`${classes.infoIcon} ${classes.infoIconTimes}`}>
            <FontAwesomeIcon icon={faClock as IconProp} />
          </span>
          <p>זמני פעילות</p>
          <strong>{happyHour?.startTime ?? '--:--'} - {happyHour?.endTime ?? '--:--'}</strong>
          <small>{formatDays(happyHour?.activeDays ?? [])}</small>
        </article>

        <article className={classes.infoCard}>
          <span className={classes.infoIcon}>
            <FontAwesomeIcon icon={faPercent as IconProp} />
          </span>
          <p>הנחה</p>
          <strong>{happyHour?.discountPct ?? 0}% על המחיר המלא</strong>
        </article>

        <article className={classes.infoCard}>
          <span className={`${classes.infoIcon} ${classes.infoIconCategory}`}>
            <FontAwesomeIcon icon={faTags as IconProp} />
          </span>
          <p>קטגוריות פעילות</p>
          <strong>{categoryNames || 'לא נבחרו קטגוריות'}</strong>
        </article>

        <article className={`${classes.infoCard} ${classes.timerCard}`}>
          <span className={classes.timerIcon}>
            <FontAwesomeIcon icon={faStopwatch as IconProp} />
          </span>
          <p>זמן שנותר</p>
          <strong>{remainingLabel}</strong>
        </article>
      </section>

      <section className={classes.questionCard}>
        <strong>האם לכבות את <span>Happy Hour</span> לכל המכשירים?</strong>
        <p className={classes.warning}>
          <FontAwesomeIcon icon={faTriangleExclamation as IconProp} />
          כיבוי המבצע יבטל את ההנחה מיידית בכל הקופות ולא ניתן לבטל את הפעולה
        </p>
      </section>

      <footer className={classes.footer}>
        <Button
          variant="confirm"
          fullWidth
          className={classes.darkActionButton}
          onClick={handleDeactivate}
          loading={isSaving}
          dataTestId="happy-hour-active-deactivate"
        >
          <span className={classes.powerButtonText}>
            <FontAwesomeIcon icon={faPowerOff as IconProp} />
            כבה Happy Hour
          </span>
        </Button>
        <Button variant="cancel" fullWidth onClick={onClose} disabled={isSaving} dataTestId="happy-hour-active-cancel">
          ביטול
        </Button>
      </footer>
      </div>
    </Modal>
  );
};
